import { useEffect, useState } from 'react';

/*
  useMediaQuery(query) — true while the CSS media query matches; re-renders when it changes.
  False where matchMedia is missing (jsdom, very old browsers), so the desktop layout is the default.

    const isPhone = useIsPhone();                       // < 640px (Tailwind `sm`)
    const wide = useMediaQuery('(min-width: 1280px)');

  Prefer plain responsive classes (max-sm:…, md:…) — use this only when the markup itself differs
  (a card list instead of a table, a bottom sheet instead of a popover).
*/
function read(query) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return Boolean(window.matchMedia(query)?.matches);
  } catch {
    return false;
  }
}

export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => read(query));
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    if (!list) return undefined;
    const update = () => setMatches(Boolean(list.matches));
    update();
    if (list.addEventListener) list.addEventListener('change', update);
    else list.addListener?.(update);
    return () => {
      if (list.removeEventListener) list.removeEventListener('change', update);
      else list.removeListener?.(update);
    };
  }, [query]);
  return matches;
}

/** Phone width (below Tailwind `sm`, 640px). */
export const useIsPhone = () => useMediaQuery('(max-width: 639px)');

export default useMediaQuery;
